<xemex-dialog-shell [title]="'installations.create.title' | translate">
  <div class="flex flex-col gap-5">
    <p class="m-0 text-text-secondary">{{ 'installations.create.intro' | translate }}</p>

    @if (data.location) {
      <xemex-text-field
        [label]="'installations.create.location' | translate"
        [readonly]="true"
        [formControl]="locationControl"
      />
    } @else {
      <mat-form-field appearance="fill" floatLabel="always" class="w-full">
        <mat-label class="text-body text-neutral">
          {{ 'installations.create.location' | translate }}
        </mat-label>
        <input
          type="text"
          matInput
          data-testid="location-search"
          [formControl]="locationControl"
          [matAutocomplete]="locationAuto"
          [placeholder]="'installations.create.locationPlaceholder' | translate"
        />
        <mat-autocomplete
          #locationAuto="matAutocomplete"
          [displayWith]="displayLocation"
          (optionSelected)="onLocationSelected($event)"
        >
          @if (searching()) {
            <mat-option disabled>{{ 'installations.create.searching' | translate }}</mat-option>
          } @else if (searchFailed()) {
            <mat-option disabled>{{ 'installations.create.searchFailed' | translate }}</mat-option>
          } @else if (searched() && options().length === 0) {
            <mat-option disabled>{{ 'installations.create.noLocations' | translate }}</mat-option>
          } @else {
            @for (option of options(); track option.locationId) {
              <mat-option [value]="option">{{ option.label }}</mat-option>
            }
          }
        </mat-autocomplete>
      </mat-form-field>
      @if (canAddLocation()) {
        <div class="-mt-3">
          <xemex-text-button
            type="button"
            icon="add"
            data-testid="new-location"
            (click)="addLocation()"
          >
            {{ 'installations.create.newLocation' | translate }}
          </xemex-text-button>
        </div>
      }
      @if (createdLocationMissing()) {
        <output class="block text-text-secondary" data-testid="new-location-missing">
          {{ 'installations.create.newLocationNotFound' | translate }}
        </output>
      }
    }

    @if (refusal(); as refused) {
      @if (refused.place === 'location') {
        <p class="m-0 text-text-error" role="alert" data-testid="location-error">
          {{ refused.messageKey | translate }}
          @if (refused.linksToLocation && selected(); as location) {
            <a
              class="text-text-link"
              data-testid="open-location"
              [routerLink]="['/', APP_PATHS.Locations, location.locationId]"
              (click)="cancel()"
            >
              {{ 'installations.create.openLocation' | translate }}
            </a>
          }
        </p>
      }
    }

    <div>
      <xemex-select-field
        [label]="'installations.create.profile' | translate"
        [required]="true"
        [invalid]="presetsFailed()"
      >
        <mat-select
          data-testid="profile-select"
          [hideSingleSelectionIndicator]="true"
          [disabled]="presetsFailed()"
          [value]="presetCode()"
          (valueChange)="presetCode.set($event)"
        >
          @for (preset of presets(); track preset.preset) {
            <mat-option [value]="preset.preset">{{ preset.displayName }}</mat-option>
          }
        </mat-select>
      </xemex-select-field>
      @if (presetsFailed()) {
        <p class="m-0 mt-1 text-text-error" role="alert" data-testid="profile-error">
          {{ 'installations.create.profileLoadFailed' | translate }}
        </p>
      }
    </div>

    @if (canLinkDevice()) {
      <mat-form-field appearance="fill" floatLabel="always" class="w-full">
        <mat-label class="text-body text-neutral">
          {{ 'installations.create.device' | translate }}
        </mat-label>
        <input
          type="text"
          matInput
          data-testid="device-search"
          [formControl]="deviceControl"
          [matAutocomplete]="deviceAuto"
          [placeholder]="'installations.create.devicePlaceholder' | translate"
        />
        <mat-hint>{{ 'installations.create.deviceHint' | translate }}</mat-hint>
        <mat-autocomplete
          #deviceAuto="matAutocomplete"
          [displayWith]="displayDevice"
          (optionSelected)="onDeviceSelected($event)"
        >
          @if (deviceSearching()) {
            <mat-option disabled>{{
              'installations.create.deviceSearching' | translate
            }}</mat-option>
          } @else if (deviceSearchFailed()) {
            <mat-option disabled>
              {{ 'installations.create.deviceSearchFailed' | translate }}
            </mat-option>
          } @else if (deviceSearched() && deviceOptions().length === 0) {
            <mat-option disabled>{{ 'installations.create.noDevices' | translate }}</mat-option>
          } @else {
            @for (option of deviceOptions(); track option.deviceId) {
              <mat-option [value]="option">{{ option.ldn }}</mat-option>
            }
          }
        </mat-autocomplete>
      </mat-form-field>
    }

    <xemex-text-field
      [label]="'installations.details.name' | translate"
      [placeholder]="'installations.create.namePlaceholder' | translate"
      [invalid]="nameTooLong()"
      [errorText]="'installations.details.nameTooLong' | translate: { max: NAME_MAX_LENGTH }"
      [formControl]="nameControl"
    />

    <xemex-text-field
      [label]="'installations.details.remarks' | translate"
      [formControl]="remarksControl"
    />

    @if (refusal(); as refused) {
      @if (refused.place === 'dialog') {
        <p class="m-0 text-text-error" role="alert" data-testid="dialog-error">
          {{ refused.messageKey | translate }}
        </p>
      }
    }
  </div>

  <div actions class="flex w-full items-center justify-end gap-3">
    <xemex-text-button (click)="cancel()">
      {{ 'general.btnCancel' | translate }}
    </xemex-text-button>
    <xemex-filled-button [disabled]="!canSave()" [loading]="isSaving()" (click)="save()">
      {{ 'general.btnCreate' | translate }}
    </xemex-filled-button>
  </div>
</xemex-dialog-shell>
